import { CheckCircle, Group, LockClock } from "@mui/icons-material";
import { Box, Button, Step, StepLabel, Stepper, Tooltip, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { cloneElement, useEffect, useMemo, useRef, useState } from "react";
import { gateway } from "../../../../gateway";
import { useDispatch } from "react-redux";
import { useModals } from "../../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import {
  clearLastCreatedJobId,
  resetJobCreateStepper,
  setContractors,
  setEmployees,
  setEventScheduler,
  setSupervisors,
} from "../../../../redux/features/jobCreateSlice";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { JobCreateDraftNote } from "../../../clients/Clients-notes/interfaces/notes.interface";
import { workforceValidator } from "../../../employees/validator/workforce.validator";
import { JobNotesCard } from "../../../jobs/jobs-details/components/JobNotesCard";
import { JobApproved } from "../../../jobs/jobs-list/jobs-layout/components/Approved/JobApproved";
import { eventSchedulerValidator } from "../../../quotes/quote-create-stepper/next-prev/validators/eventSchedulerValidator";
import { EMode } from "../../../shared/components/features/event-scheduler/enums/event.enums";
import { EventSchedulerView } from "../../../shared/components/features/event-scheduler/view/EventSchedulerView";
import { useCompanyInfo } from "../../../shared/hooks/useCompany";
import { useExtendedLoading } from "../../../shared/hooks/useExtendedLoading";
import { colors } from "../../../ui/colors";
import { SASButtonStyles } from "../../../ui/styles/inputStyles";
import { clearTimelineClickedTime } from "../../config/timeline.constants";
import { applyTimeZoneToEventBase, transformEventBase } from "../../JobCreate/helper/transforEventScheduler";
import { useEventScheduler } from "../../JobCreate/hooks/useCreateJobWithoutQuote";
import { JobLoadingStatus } from "../../JobCreate/JobLoadingStatus";
import { useVisitMutation } from "../hooks/useVisitMutation";

interface Props {
  jobId: number;
  closeModalKey?: string;
  initialStep?: number;
  onComplete?: () => void;
  defaultWorkforce?: any[];
}

export const VisitStepper = ({ jobId, closeModalKey, initialStep = 0, onComplete, defaultWorkforce = [] }: Props) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(initialStep);
  const [draftNotes, setDraftNotes] = useState<JobCreateDraftNote[]>([]);
  const [pickedWorkforce, setPickedWorkforce] = useState<any>(null);
  const [pickerCrew, setPickerCrew] = useState<any[] | null>(null);
  const [mustChooseWorkforce, setMustChooseWorkforce] = useState(false);
  const [pickerVersion, setPickerVersion] = useState(0);
  const didHydrateDefaultWorkforceRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createJob: sendToEventScheduler } = useEventScheduler();

  const handlePayloadEventChange = (payload) => {
    dispatch(setEventScheduler(payload));
  };

  const companyData = useCompanyInfo();

  const companyTimeZone = companyData.data?.timeZone ?? null;
  const queryClient = useQueryClient();

  const { mutateAsync: createJobEventMutation, isPending } = useVisitMutation();
  const { closeByKey } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const { eventScheduler, workforce } = useAppSelector((state) => state.jobCreateStepper);

  const getDefaultWorkerRole = (worker: any) => {
    return (worker?.WorkforcesByJobs?.jobWorkforceRole ?? worker?.workforceRole ?? worker?.role ?? worker?.type ?? "").toLowerCase();
  };

  const normalizeDefaultWorker = (worker: any) => {
    const role = getDefaultWorkerRole(worker);

    return {
      ...worker,
      role,
      type: role,
      workforceRole: worker?.workforceRole ?? role,
    };
  };

  const hydratedDefaultWorkforce = useMemo(() => {
    const normalizedWorkers = (defaultWorkforce ?? []).map((worker) => {
      const role = (worker?.WorkforcesByJobs?.jobWorkforceRole ?? worker?.workforceRole ?? worker?.role ?? worker?.type ?? "").toLowerCase();

      return {
        ...worker,
        role,
        type: role,
        workforceRole: worker?.workforceRole ?? role,
      };
    });

    return {
      supervisors: normalizedWorkers.filter((worker) => worker.role === "supervisor"),
      employees: normalizedWorkers.filter((worker) => worker.role === "employee"),
      contractors: normalizedWorkers.filter((worker) => worker.role === "contractor"),
    };
  }, [defaultWorkforce]);

  const flatDefaultWorkforce = useMemo(() => {
    return [...hydratedDefaultWorkforce.supervisors, ...hydratedDefaultWorkforce.employees, ...hydratedDefaultWorkforce.contractors];
  }, [hydratedDefaultWorkforce]);

  const hasDefaultWorkforce = flatDefaultWorkforce.length > 0;

  useEffect(() => {
    if (didHydrateDefaultWorkforceRef.current) return;
    if (!hasDefaultWorkforce) return;
    if (workforceValidator(workforce)) return;

    dispatch(setSupervisors(hydratedDefaultWorkforce.supervisors));
    dispatch(setEmployees(hydratedDefaultWorkforce.employees));
    dispatch(setContractors(hydratedDefaultWorkforce.contractors));

    didHydrateDefaultWorkforceRef.current = true;
  }, [dispatch, hasDefaultWorkforce, hydratedDefaultWorkforce, workforce]);

  const showLoading = useExtendedLoading(isSubmitting || isPending, 1200);

  const availabilityWindow = useMemo(() => {
    try {
      const base = transformEventBase(eventScheduler);
      if (!base || base.mode !== "SINGLE_EXECUTION" || !base.startDate || !base.duration) return null;
      const parsed = DateTime.fromISO(base.startDate, { setZone: true });
      const zone = companyTimeZone || "UTC";
      const start = DateTime.fromObject(
        {
          year: parsed.year,
          month: parsed.month,
          day: parsed.day,
          hour: parsed.hour,
          minute: parsed.minute,
          second: parsed.second,
          millisecond: parsed.millisecond,
        },
        { zone },
      );
      const minutes = Number(base.duration);
      if (!parsed.isValid || !start.isValid || !Number.isFinite(minutes) || minutes <= 0) return null;
      return {
        startAt: start.toUTC().toISO(),
        endAt: start.plus({ minutes }).toUTC().toISO(),
      };
    } catch {
      return null;
    }
  }, [companyTimeZone, eventScheduler]);

  const steps = [
    {
      label: "Select Workforce",
      icon: <Group />,
      isValid: () => workforceValidator(workforce) === true || (hasDefaultWorkforce && !mustChooseWorkforce),
      content: (
        <JobApproved
          key={pickerVersion}
          data={null}
          mode="create"
          currentWf={pickerCrew ?? flatDefaultWorkforce}
          availabilityWindow={availabilityWindow}
          onWorkforceChange={setPickedWorkforce}
        />
      ),
    },
    {
      label: "Select Scheduled Time",
      icon: <LockClock />,
      isValid: () => eventSchedulerValidator(eventScheduler),
      content: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 1 }}>
          <EventSchedulerView
            onPayloadChange={handlePayloadEventChange}
            canSkip={false}
            multipleDates={false}
            disableJsonPayload
            enabledModes={[EMode.SINGLE_EXECUTION, EMode.REPETITIVE]}
          />
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              border: `1px solid ${colors.gray[200]}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <JobNotesCard
              mode="draft"
              maxNotes={1}
              defaultNoteType="visit"
              showTypePicker={false}
              draftNotes={draftNotes}
              onDraftNotesChange={setDraftNotes}
              placeholder="Type a note for this visit..."
              emptyMessage="Optional. Add a note or instructions for this visit."
              sx={{ px: 0, py: 0 }}
            />
          </Box>
        </Box>
      ),
    },
  ];

  const isCurrentStepValid = useMemo(() => steps[step].isValid(), [step, workforce, eventScheduler, hasDefaultWorkforce, mustChooseWorkforce]);
  const isLastStep = step === steps.length - 1;
  const isFirstStep = step === 0;
  const eventBase = transformEventBase(eventScheduler);

  const handleFinish = async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const workforceToSend = pickedWorkforce ?? hydratedDefaultWorkforce;
      const selectedWorkers = [
        ...(workforceToSend.supervisors ?? []),
        ...(workforceToSend.employees ?? []),
        ...(workforceToSend.contractors ?? []),
      ];

      if (availabilityWindow?.startAt && availabilityWindow?.endAt && selectedWorkers.length) {
        try {
          const token = sessionStorage.getItem("token");
          const unavailable = await gateway.get("/workforce/unavailable", {
            headers: { Authorization: token },
            params: availabilityWindow,
          });
          const blocked = new Set<string>(unavailable.data?.data || []);
          const removed = selectedWorkers.filter((worker) => blocked.has(worker.uid));

          if (removed.length) {
            const withoutBlocked = {
              supervisors: (workforceToSend.supervisors ?? []).filter((worker) => !blocked.has(worker.uid)),
              employees: (workforceToSend.employees ?? []).filter((worker) => !blocked.has(worker.uid)),
              contractors: (workforceToSend.contractors ?? []).filter((worker) => !blocked.has(worker.uid)),
            };
            const names = removed.map((worker) => `${worker.firstName || ""} ${worker.lastName || ""}`.trim()).filter(Boolean).join(", ");
            setPickedWorkforce(withoutBlocked);
            setPickerCrew([...withoutBlocked.supervisors, ...withoutBlocked.employees, ...withoutBlocked.contractors]);
            dispatch(setSupervisors(withoutBlocked.supervisors));
            dispatch(setEmployees(withoutBlocked.employees));
            dispatch(setContractors(withoutBlocked.contractors));
            setMustChooseWorkforce(true);
            setPickerVersion((version) => version + 1);
            setStep(0);
            showSnackbar(
              names
                ? `${names} is not free at that time, so they stay off this visit. Choose who should take it.`
                : "The default workforce is not free at that time. Choose who should take this visit.",
              "warning",
            );
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            return;
          }
        } catch {
          // The visit request still checks the same conflict and returns here to choose.
        }
      }

      const eventWithTimeZone = applyTimeZoneToEventBase(eventBase, companyTimeZone);
      const eventSchedulerResponse = await sendToEventScheduler(eventWithTimeZone);
      const eventUid = eventSchedulerResponse?.uid;

      const notesPayload = draftNotes
        .filter((note) => note?.content?.trim())
        .map((note) => ({
          content: note.content.trim(),
          type: note.type || "visit",
          pinned: Boolean(note.pinned),
        }));

      const payload = {
        workforce: workforceToSend,
        schedule: eventBase,
        jobId,
        eventUid,
        ...(notesPayload.length > 0 ? { notes: notesPayload } : {}),
      };

      await createJobEventMutation(payload);

      // Guarda temporalmente el evento recién creado para que la lista de visitas
      // pueda enfocarlo cuando TanStack Query termine de cargar la respuesta actualizada.
      if (eventUid) {
        sessionStorage.setItem(`focus-job-event-${jobId}`, eventUid);
      }

      clearTimelineClickedTime();
      dispatch(clearLastCreatedJobId());
      dispatch(resetJobCreateStepper());
      onComplete?.();
      closeByKey(closeModalKey ?? "Job-Open-New-Event");

      queryClient.invalidateQueries({
        queryKey: ["job-events", jobId],
      });

      queryClient.invalidateQueries({
        queryKey: ["job-events"],
      });

      queryClient.invalidateQueries({
        queryKey: ["widget-job-events"],
      });

      queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });

      queryClient.invalidateQueries({
        queryKey: ["job", jobId],
      });

      queryClient.invalidateQueries({
        queryKey: ["job-detail", jobId],
      });

      queryClient.invalidateQueries({
        queryKey: ["job-self", jobId],
      });
    } catch (error: any) {
      const blockedUids = Array.isArray(error?.response?.data?.details) ? error.response.data.details : [];
      const workforceToSend = pickedWorkforce ?? hydratedDefaultWorkforce;
      if (error?.response?.status === 409 && blockedUids.length) {
        const blocked = new Set<string>(blockedUids);
        const withoutBlocked = {
          supervisors: (workforceToSend.supervisors ?? []).filter((worker) => !blocked.has(worker.uid)),
          employees: (workforceToSend.employees ?? []).filter((worker) => !blocked.has(worker.uid)),
          contractors: (workforceToSend.contractors ?? []).filter((worker) => !blocked.has(worker.uid)),
        };
        setPickedWorkforce(withoutBlocked);
        setPickerCrew([...withoutBlocked.supervisors, ...withoutBlocked.employees, ...withoutBlocked.contractors]);
        dispatch(setSupervisors(withoutBlocked.supervisors));
        dispatch(setEmployees(withoutBlocked.employees));
        dispatch(setContractors(withoutBlocked.contractors));
        setMustChooseWorkforce(true);
        setPickerVersion((version) => version + 1);
        setStep(0);
        showSnackbar("That workforce is not free at this time. Choose someone else for this visit.", "warning");
      } else {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "The visit could not be created.";
        showSnackbar(message, "error");
      }
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!isCurrentStepValid || isSubmittingRef.current) return;

    if (isLastStep) {
      void handleFinish();
      return;
    }

    setStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (isFirstStep || isSubmittingRef.current) return;
    setStep((prev) => prev - 1);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Stepper */}
      <Stepper
        activeStep={step}
        alternativeLabel
        sx={{
          pt: 1,
          pb: 2,
          mb: 1,
          flexShrink: 0,
          "& .MuiStepConnector-line": {
            borderColor: colors.orange[200],
            borderWidth: 2,
          },
        }}
      >
        {steps.map((s, index) => {
          const completed = step > index;
          const isActive = step === index;

          const Icon = completed ? <CheckCircle sx={{ fontSize: "1.25rem" }} /> : cloneElement(s.icon, { sx: { fontSize: "1.25rem" } });

          return (
            <Step key={s.label} completed={completed}>
              <Tooltip title={s.label} arrow>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        bgcolor: completed ? colors.orange[100] : isActive ? colors.orange.base : colors.gray[100],
                        color: completed ? colors.orange.base : isActive ? "#fff" : colors.gray[400],
                        border: `2px solid ${completed ? colors.orange[300] : isActive ? colors.orange.base : colors.gray[200]}`,
                        transition: "all 0.2s ease",
                      }}
                    >
                      {Icon}
                    </Box>
                  )}
                  sx={{
                    "& .MuiStepLabel-label": {
                      mt: 0.5,
                      typography: "caption",
                      color: isActive ? colors.orange.base : completed ? colors.gray[600] : colors.gray[400],
                      fontWeight: isActive || completed ? 600 : 400,
                    },
                  }}
                >
                  <Typography variant="caption" noWrap>
                    {s.label}
                  </Typography>
                </StepLabel>
              </Tooltip>
            </Step>
          );
        })}
      </Stepper>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 0.5 }}>{steps[step].content}</Box>
      {showLoading && <JobLoadingStatus />}

      {/* Navigation */}
      <Box
        sx={{
          mt: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          pt: 2,
          pb: 0.5,
          flexShrink: 0,
          borderTop: `1px solid ${colors.gray[100]}`,
        }}
      >
        <Button type="button" sx={SASButtonStyles} disabled={isFirstStep || isSubmitting} onClick={handlePrev}>
          Back
        </Button>

        <Button
          type="button"
          sx={SASButtonStyles}
          variant="contained"
          onClick={handleNext}
          onKeyDown={(event) => {
            if (event.repeat && isLastStep) {
              event.preventDefault();
            }
          }}
          disabled={!isCurrentStepValid || isSubmitting}
        >
          {isLastStep ? "Finish" : "Next"}
        </Button>
      </Box>
    </Box>
  );
};
